import React, { useState } from "react";
import { ClockCircleOutlined, DownloadOutlined, FileProtectOutlined } from "@ant-design/icons";
import { DateField } from "@refinedev/antd";
import { BaseRecord, useApiUrl, useGetIdentity, useMany, useTranslate } from "@refinedev/core";
import { Button, Space, Table, Card, Tag, Flex, theme, message } from "antd";
import Column from "antd/es/table/Column";
import { axiosInstance } from "../../authProvider";

const { useToken } = theme;

export const InvoiceList = () => {
    const { token } = useToken();
    const _t = useTranslate();
    const apiUrl = useApiUrl();
    const { data: user } = useGetIdentity();
    const accounts = useMany({ resource: 'auth/my-account', ids: [] });
    const account = accounts?.data?.data;
    //const account = useOne({ resource: 'accounts', id: user?.account?.id });
    const invoices = (account as any)?.invoices;
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
    interface InvoiceDownloadData {
        qontoInvoiceUrl?: string | null;
    }
    const downloadLocalInvoicePdf = async (invoiceId: number): Promise<void> => {
        const response = await axiosInstance.get(`${apiUrl}/invoices/${invoiceId}/pdf`, {
            responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const objectUrl = window.URL.createObjectURL(blob);
        const linkElement = document.createElement("a");
        linkElement.href = objectUrl;
        linkElement.download = `invoice-${invoiceId}.pdf`;
        document.body.appendChild(linkElement);
        linkElement.click();
        document.body.removeChild(linkElement);
        window.URL.revokeObjectURL(objectUrl);
    };

    const handleDownloadInvoice = async (invoiceId: number) => {
        setDownloadingInvoiceId(invoiceId);
        try {
            const invoiceResponse = await axiosInstance.get<InvoiceDownloadData>(`${apiUrl}/invoices/${invoiceId}`);
            const qontoInvoiceUrl = invoiceResponse.data?.qontoInvoiceUrl;
            if (qontoInvoiceUrl) {
                const openedWindow = window.open(
                    qontoInvoiceUrl,
                    "_blank",
                    "noopener,noreferrer",
                );
                if (!openedWindow) {
                    throw new Error("popup_blocked");
                }
                return;
            }
            await downloadLocalInvoicePdf(invoiceId);
        } catch (error) {
            message.error(_t('invoices.errors.downloadFailed'));
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <Card className="shadow-lg" bodyStyle={{ padding: 0 }}>
                <div className="px-8 pt-6">
                    <Flex className="text-xl mb-4">
                        <FileProtectOutlined className="mr-2" style={{ color: token.colorPrimary }} /> 
                        {_t("invoices.titles.list")}
                    </Flex>
                </div>
                <Table 
                    dataSource={invoices} 
                    rowKey="id" 
                    size="small"
                    className="px-6"
                >
                    <Column 
                        title={_t("invoices.fields.createdAt")} 
                        dataIndex="createdAt"
                        render={(value: any) =>
                            <Tag 
                                icon={<ClockCircleOutlined />} 
                                style={{ 
                                    backgroundColor: token.colorBgElevated,
                                    color: token.colorText,
                                    borderColor: token.colorBorderBg
                                }}
                            >
                                <DateField 
                                    value={value} 
                                    format={"DD MMM HH:mm"}
                                    style={{ color: token.colorPrimary }}
                                />
                            </Tag>
                        }
                        width={280} 
                    />

                    <Table.Column
                        title={_t("invoices.fields.totalInclVat")}
                        dataIndex="totalInclTax"
                        render={(value: any) => `${(value/100).toFixed(0)} €`}
                    />

                    <Table.Column
                        title={_t("table.actions")}
                        dataIndex="actions"
                        render={(_, invoice: BaseRecord) => (
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    loading={downloadingInvoiceId === invoice.id}
                                    onClick={() => handleDownloadInvoice(invoice.id as number)}
                                >
                                    {_t('invoices.titles.download')}
                                </Button>
                            </Space>
                        )}
                    />
                </Table>
            </Card>
        </div>
    )
}